// ----------------------------------------------------------------------------
// Theme store. Two independent preferences:
//   - 'admin'  : the admin app and its auth screens. Defaults to the OS setting.
//   - 'member' : the /upload Song Studio page. Defaults to dark, as it always has.
// Each is persisted in localStorage. A component that calls useTheme() applies
// that scope's mode to <html> while mounted (both the `.dark` class the legacy
// components key off and the `data-theme` attribute the tokens also accept) and
// restores whatever was there before when it unmounts, so /upload going dark
// never leaks into the admin app.
// ----------------------------------------------------------------------------

import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemeScope = 'admin' | 'member';

export const THEME_STORAGE_KEYS: Record<ThemeScope, string> = {
  admin: 'wp-theme',
  member: 'wp-theme-upload',
};

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const systemQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

// Holds a choice made while storage is blocked, so the toggle still sticks for
// this page session instead of snapping back to the default on the next read.
const unsaved: Partial<Record<ThemeScope, ThemeMode>> = {};

export function readTheme(scope: ThemeScope): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEYS[scope]);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage blocked (private mode, sandboxed iframe): fall through to defaults.
  }
  if (unsaved[scope]) return unsaved[scope]!;
  if (scope === 'member') return 'dark';
  return systemQuery()?.matches ? 'dark' : 'light';
}

export function writeTheme(scope: ThemeScope, mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEYS[scope], mode);
    delete unsaved[scope];
  } catch {
    // Not persisted; remember it in memory so the in-page toggle still works.
    unsaved[scope] = mode;
  }
  applyTheme(mode);
  emit();
}

export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.setAttribute('data-theme', mode);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const mq = systemQuery();
  // Follow OS changes while the admin preference is still unset.
  mq?.addEventListener?.('change', listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key && Object.values(THEME_STORAGE_KEYS).includes(e.key)) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    mq?.removeEventListener?.('change', listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useTheme(scope: ThemeScope = 'admin') {
  const mode = useSyncExternalStore(subscribe, () => readTheme(scope), () => readTheme(scope));

  // Capture the page's theme before we touch it, and put it back on unmount.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    const prevAttr = root.getAttribute('data-theme');
    return () => {
      root.classList.toggle('dark', hadDark);
      if (prevAttr === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', prevAttr);
    };
  }, []);

  useLayoutEffect(() => {
    applyTheme(mode);
  }, [mode]);

  const toggle = useCallback(() => {
    writeTheme(scope, readTheme(scope) === 'dark' ? 'light' : 'dark');
  }, [scope]);

  return { mode, isDark: mode === 'dark', toggle, setMode: (m: ThemeMode) => writeTheme(scope, m) };
}
